"use client"
import analysisImage from './analysis.png';
import sampleImage from './sample.png';
import { useState, useEffect } from "react"
import { Landmark, Building, Briefcase, Sparkle, FileText, ChartNoAxesCombined } from "lucide-react"
import api from '../api/api';

export default function Home() {
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isDemoModal, setIsDemoModal] = useState(false)
  const [showNotification, setShowNotification] = useState(false)
  const [errMsg, setErrMsg] = useState('')
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    company_name: "",
    position: "",
    request_type: "",
    message: "",
  })

  // 通知を表示して自動的に閉じる
  useEffect(() => {
    if (showNotification) {
      const timer = setTimeout(() => {
        setShowNotification(false)
      }, 3000)
      return () => clearTimeout(timer)
    }
  }, [showNotification])

  const handleOpenModal = (modalType) => {
    setIsDemoModal(modalType==="demo")
    setFormData((prev) => ({
      ...prev,
      ["request_type"]: modalType,
    }))
    setIsModalOpen(true)
    // モーダルが開いている間はスクロールを無効化
    document.body.style.overflow = "hidden"
  }

  const handleCloseModal = () => {
    setIsModalOpen(false)
    // モーダルが閉じたらスクロールを有効化
    document.body.style.overflow = "auto"
  }

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }))
  }

  const register = async () => {
    try {
      const formattedFormData = { ...formData }; // スプレッド構文でコピー

      // 先頭が "0" のときのみ変換（080 → +81-80）
      if (formattedFormData.phone.startsWith("0")) {
        formattedFormData.phone = "+81-" + formattedFormData.phone.slice(1);
      }
      const response = await api.post(`${process.env.REACT_APP_API_URL}/accounts/api/request/`, formattedFormData);
      setShowNotification(true)  
      setIsModalOpen(false)
      document.body.style.overflow = "auto"
      // フォームをリセットしてモーダルを閉じる
      setFormData({
        name: "",
        email: "",
        phone: "",
        company_name: "",
        position: "",
        message: "",
        request_type: "",
      })
    } catch (error) {
        setErrMsg('申し訳ございません。処理の途中でエラーが発生しました。原因を調査し、対応できるように改善します。');
    }
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    register()
  }

  // モーダル外のクリックでモーダルを閉じる
  const handleModalBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      handleCloseModal()
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100 w-full overflow-y-auto">
      {/* ヘッダー */}
      <header className="shadow-sm bg-gray-900">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <div className="flex items-center">
            <h1 className="text-2xl font-bold text-white">Insight Note</h1>
          </div>
          <nav>
            <ul className="flex space-x-6">
              <li>
                <a href="#features" className="text-white hover:text-gray-200">
                  特徴
                </a>
              </li>
                <li>
                  <a href="#main-function" className="text-white hover:text-gray-200">
                    機能
                  </a>
                </li>
                {/* <li>
                <a href="#how-it-works" className="text-white hover:text-gray-200">
                  使い方
                </a>
              </li> */}
              <li>
                <a href="#usage" className="text-white hover:text-gray-200">
                  ご利用シーン
                </a>
              </li>
            </ul>
          </nav>
          <button
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md transition duration-300"
            onClick={() => handleOpenModal("request")}
          >
            資料請求
          </button>
        </div>
      </header>

      {/* ヒーローセクション */}
      <section className="py-20 px-4">
        <div className="container mx-auto max-w-6xl flex flex-col md:flex-row items-center">
          <div className="md:w-3/5 mb-10 md:mb-0">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 leading-tight mb-4">
              企業分析に特化した<span className="text-blue-600">生成AI</span>で<br />
              意思決定を加速する
            </h2>
            <p className="text-xl text-gray-600 mb-8">
              Insight Noteは、生成AIや機械学習を駆使し、膨大な企業・金融関連データから重要なインサイトを抽出し、
              ビジネスの意思決定を支援するAIプラットフォームです。
            </p>
            <div className="flex flex-col sm:flex-row space-y-4 sm:space-y-0 sm:space-x-4">
              <button
                className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-md text-lg font-medium transition duration-300"
                onClick={() => handleOpenModal("request")}
              >
                資料請求
              </button>
              <button className="border border-gray-300 hover:border-gray-400 text-gray-700 px-6 py-3 rounded-md text-lg font-medium transition duration-300">
                <a href="https://finosight-analytics.co.jp/contact/">お問合せ</a>
              </button>
            </div>
          </div>
          <div className="bg-transparent md:w-2/5">
            <div className="p-2">
              <img
                src={sampleImage || "/placeholder.svg"}
                alt="Insight Noteのダッシュボード"
                className="rounded-lg w-full"
              />
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4 max-w-6xl">
          <h2 className="text-4xl font-bold text-center text-gray-900 mb-8">
            迅速な意思決定ができていますか？
          </h2>

          <p className="text-lg text-gray-700 text-center mb-12 max-w-4xl mx-auto">
          現代は、時間の流れが加速し、世の中にあふれる情報量もかつてないほど膨大になっています。
          ビジネスにおいては、その中から自社にとって本当に重要な情報を見極め、戦略的な意思決定につなげることが求められています。
          しかし、企業分析に多くの時間をかけて情報を収集・整理していると、意思決定のタイミングを逃し、競争優位を失うリスクさえあります。
          Insight Note は、こうした課題を根本から解決します。
          膨大な情報の中から必要なインサイトを瞬時に抽出し、スピーディかつ的確な意思決定をサポートします。
          </p>

          <h3 className="text-3xl font-semibold text-center text-gray-800 mb-12 mt-[80px]">こんな世界を実現します
          </h3>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white p-8 rounded-xl shadow-md text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-6 mx-auto">
              <Sparkle />
              </div>
              <p className="text-lg text-gray-800 font-bold mb-4">迅速な戦略立案と意思決定</p>
              <p className="text-gray-600">インターネット情報ではなく、信頼性の高い企業の一次情報と高度な分析結果をもとに、AIが戦略立案をサポートします。
              <strong>意思決定のスピードと精度が飛躍的に向上し、企業の競争力を高めます</strong>。</p>
            </div>
            <div className="bg-white p-8 rounded-xl shadow-md text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-6 mx-auto">
                <ChartNoAxesCombined />
              </div>
              <p className="text-lg text-gray-800 font-bold mb-4">分析作業を自動でかつ高精度に</p>
              <p className="text-gray-600">企業分析に特化したAIが分析を行うことで、今までExcelで分析したり、スライドにまとめていた<strong>作業が数分で終わります</strong>。
              様々な情報を多面的に分析することで、<strong>クオリティの高い分析を自動で行うことができます</strong>。</p>
            </div>
            <div className="bg-white p-8 rounded-xl shadow-md text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-6 mx-auto">
                <FileText />
              </div>
              <p className="text-lg text-gray-800 font-bold mb-4">どんな情報も瞬時に収集</p>
              <p className="text-gray-600">決算資料やプレスリリースなど膨大な情報の中から、AIが必要な情報だけを自動で抽出。
              もう、時間をかけて資料を探し回る必要はありません。また、<strong>競合等に関する重要な動きも逃さず把握できるため、情報の見落としを防げます</strong>。</p>
            </div>
          </div>
        </div>
      </section>

      {/* 特徴セクション */}
      <section id="features" className="py-16 bg-white">
        <div className="container mx-auto px-4 max-w-6xl">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">Insight Noteの特徴</h2>

          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-gray-50 p-8 rounded-xl">
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-4">
                <svg
                  className="w-6 h-6 text-blue-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
              <h3 className="text-xl font-semibold mb-3">分析時間最大99%削減</h3>
              <p className="text-gray-600">
                情報収集・分析ではなく、意思決定に時間を使えます。AIが自動的に関連データを収集・整理し、分析、戦略立案支援まで行います。
                あなたは意思決定に集中できます。
              </p>
            </div>

            <div className="bg-gray-50 p-8 rounded-xl">
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-4">
                <svg
                  className="w-6 h-6 text-blue-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                  />
                </svg>
              </div>
              <h3 className="text-xl font-semibold mb-3">高度な分析</h3>
              <p className="text-gray-600">
                様々な一次情報をもとに、AIが複雑なデータパターンを分析し、人間では見落としがちな重要なインサイトを抽出するため、誰でも専門家のような分析を行えます。
                それにより、より深い洞察に基づいた戦略的判断が可能になります。
              </p>
            </div>

            <div className="bg-gray-50 p-8 rounded-xl">
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-4">
                <svg
                  className="w-6 h-6 text-blue-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold mb-3">膨大な金融・企業データ</h3>
              <p className="text-gray-600">
                日米中の株価・財務情報だけでなく、有価証券報告書、適時開示、プレスリリース、ESG情報など1000万以上のデータを網羅し、そこから自動で分析を行います。
              </p>
            </div>

            <div className="bg-gray-50 p-8 rounded-xl">
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-4">
                <svg
                  className="w-6 h-6 text-blue-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                  />
                </svg>
              </div>
              <h3 className="text-xl font-semibold mb-3">信頼性の高い情報源</h3>
              <p className="text-gray-600">
                情報を集約したインターネット記事ではなく、企業の一次情報を基に分析するので、確かな情報に基づいた判断ができます。
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 使い方セクション */}
      <section id="main-function" className="py-16 bg-gray-50">
        <div className="container mx-auto px-4 max-w-6xl">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">機能</h2>

          <div className="flex flex-col md:flex-row items-center justify-between">
            <div className="md:w-1/2 mb-8 md:mb-0 md:pr-8">
              <div className="space-y-8">
                <div className="flex">
                  <div className="flex-shrink-0 mr-4">
                    <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center text-white font-bold">
                      1
                    </div>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold mb-2">チャット</h3>
                    <p className="text-gray-600">
                    気になる企業について、チャットで質問するだけ。<br />
                    AIが膨大な資料の中から関連情報を検索し、要点を整理してわかりやすく回答します。
                    </p>
                  </div>
                </div>
                <div className="flex">
                  <div className="flex-shrink-0 mr-4">
                    <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center text-white font-bold">
                      2
                    </div>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold mb-2">分析</h3>
                    <p className="text-gray-600">
                    企業名や関連キーワード、プレスリリースなどから企業を検索。<br/>
                    選んだ企業について、チャートや数値で直感的に可視化しながら、さらにチャットで深掘りも可能です。
                    </p>
                  </div>
                </div>
                <div className="flex">
                  <div className="flex-shrink-0 mr-4">
                    <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center text-white font-bold">
                      3
                    </div>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold mb-2">レポート作成</h3>
                    <p className="text-gray-600">
                    作成したいレポートのテーマを指定するだけでOK。<br/>
                    AIが最適な構成を設計し、必要な情報を資料から自動で抽出。数十ページにわたる高品質な調査レポートを自動作成します。
                    </p>
                  </div>
                </div>
              </div>
              {/* <div className="flex">
                <div className="flex-shrink-0 mr-4">
                  <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center text-white font-bold">
                    2
                  </div>
                </div>
                <div>
                  <h3 className="text-xl font-semibold mb-2">ダッシュボード機能</h3>
                  <p className="text-gray-600">
                  企業ごとのダッシュボードでは、財務情報や市場データに加えて、業界全体の動向も確認可能。<br/>
                  表示項目は自由にカスタマイズでき、知りたい情報に最短でアクセスできます。
                  </p>
                </div>
              </div>
              <div className="flex">
                <div className="flex-shrink-0 mr-4">
                  <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center text-white font-bold">
                    2
                  </div>
                </div>
                <div>
                  <h3 className="text-xl font-semibold mb-2">ノート機能</h3>
                  <p className="text-gray-600">
                  チャットで調べた内容をそのまま保存し、自分なりに整理・編集して残すことが可能。<br></br>
                  分析ログやインサイトを一元管理し、チーム内での共有もスムーズに。
                  </p>
                </div>
              </div> */}
            </div>

            <div className="md:w-1/2">
              <div className="p-2 rounded-xl shadow-xl">
                <img
                  src={analysisImage || "/placeholder.svg"}
                  alt="Insight Noteの使用イメージ"
                  className="rounded-lg w-full"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 使用例セクション */}
      <section id="usage" className="py-16 bg-white">
        <div className="container mx-auto px-4 max-w-6xl">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">ご利用シーン</h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-gray-50 p-6 rounded-xl">
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 bg-gray-300 rounded-full mr-4 flex justify-center items-center">
                  <Building />
                </div>
                <div>
                  <h4 className="font-semibold">経営企画部門</h4>
                </div>
              </div>
              <p className="text-gray-600">
                競合分析や市場動向の把握、戦略立案に役立てることができます。従来の情報収集 → 分析 → 戦略立案 →
                資料作成というステップを自動化することができ、コストを大幅に削減し、その後の意思決定に集中することができます。
              </p>
            </div>
            <div className="bg-gray-50 p-6 rounded-xl">
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 bg-gray-300 rounded-full mr-4 flex justify-center items-center">
                  <Landmark />
                </div>
                <div>
                  <h4 className="font-semibold">金融機関のアナリスト</h4>
                </div>
              </div>
              <p className="text-gray-600">
                一次情報をもとにした企業分析や業界分析を一瞬で行い、クオリティの高いレポートを作成します。分析したい内容も自然言語で説明したり、簡単な操作で作成することができます。
              </p>
            </div>
            <div className="bg-gray-50 p-6 rounded-xl">
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 bg-gray-300 rounded-full mr-4 flex justify-center items-center">
                  <Briefcase />
                </div>
                <div>
                  <h4 className="font-semibold">機関投資家</h4>
                </div>
              </div>
              <p className="text-gray-600">
                上場企業の投資判断のための分析を行うことができます。AIが自動で情報を収集・分析し、インサイトを導き出すことができます。ESGに対するの取り組みなども簡単にまとめることができます。
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA セクション */}
      <section className="py-20 bg-blue-600">
        <div className="container mx-auto px-4 max-w-6xl text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">ビジネスの意思決定を次のレベルへ</h2>
          <p className="text-xl text-blue-100 mb-8 max-w-3xl mx-auto">
            Insight Noteを使って、データに基づいた戦略的な意思決定を始めましょう。
          </p>
          <div className="flex flex-col sm:flex-row justify-center space-y-4 sm:space-y-0 sm:space-x-4">
            <button
              className="bg-white hover:bg-gray-100 text-blue-600 px-8 py-4 rounded-md text-lg font-medium transition duration-300"
              onClick={() => handleOpenModal("request")}
            >
              資料請求
            </button>
            <button 
              className="border border-blue-300 hover:border-blue-200 text-white px-8 py-4 rounded-md text-lg font-medium transition duration-300"
              onClick={() => handleOpenModal("demo")}
              >
              デモを依頼する
            </button>
          </div>
        </div>
      </section>

      {/* フッター */}
      <footer className="bg-gray-800 text-gray-300 py-12">
        <div className="container mx-auto px-4 max-w-6xl">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <h3 className="text-xl font-bold text-white mb-4">Insight Note</h3>
              <p className="mb-4">企業分析に特化したAIがデータをもとに意思決定を支援します。</p>
              <div className="flex space-x-4">
                <a href="#" className="text-gray-400 hover:text-white">
                  <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path d="M22.675 0h-21.35c-.732 0-1.325.593-1.325 1.325v21.351c0 .731.593 1.324 1.325 1.324h11.495v-9.294h-3.128v-3.622h3.128v-2.671c0-3.1 1.893-4.788 4.659-4.788 1.325 0 2.463.099 2.795.143v3.24l-1.918.001c-1.504 0-1.795.715-1.795 1.763v2.313h3.587l-.467 3.622h-3.12v9.293h6.116c.73 0 1.323-.593 1.323-1.325v-21.35c0-.732-.593-1.325-1.325-1.325z" />
                  </svg>
                </a>
                <a href="#" className="text-gray-400 hover:text-white">
                  <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723 10.1 10.1 0 01-3.127 1.184 4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z" />
                  </svg>
                </a>
                <a href="#" className="text-gray-400 hover:text-white">
                  <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm-2 16h-2v-6h2v6zm-1-6.891c-.607 0-1.1-.496-1.1-1.109 0-.612.492-1.109 1.1-1.109s1.1.497 1.1 1.109c0 .613-.493 1.109-1.1 1.109zm8 6.891h-1.998v-2.861c0-1.881-2.002-1.722-2.002 0v2.861h-2v-6h2v1.093c.872-1.616 4-1.736 4 1.548v3.359z" />
                  </svg>
                </a>
              </div>
            </div>

            <div>
              <h4 className="text-white font-semibold mb-4">会社情報</h4>
              <ul className="space-y-2">
                <li>
                  <a href="https://finosight-analytics.co.jp/" className="hover:text-white">
                    会社概要
                  </a>
                </li>
                <li>
                  <a href="https://finosight-analytics.co.jp/recruitment/" className="hover:text-white">
                    採用情報
                  </a>
                </li>
              </ul>
            </div>
          </div>

          <div className="border-t border-gray-700 mt-8 pt-8 text-sm text-center">
            <p>&copy; {new Date().getFullYear()} Insight Note. All rights reserved.</p>
          </div>
        </div>
      </footer>

      {/* 資料請求モーダル（カスタム実装） */}
      {isModalOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={handleModalBackdropClick}
        >
          <div
            className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6">
              <div className="mb-4">
                <h2 className="text-xl font-bold text-gray-900">{isDemoModal ? "デモ依頼フォーム" : "資料請求フォーム" } </h2>
                <p className="text-gray-600 mt-1">
                  以下のフォームに必要事項をご記入ください。担当者より折り返しご連絡いたします。
                </p>
              </div>

              <form onSubmit={handleSubmit}>
                <div className="space-y-4">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                      お名前 <span className="text-red-500">*</span>
                    </label>
                    <input
                      id="name"
                      name="name"
                      type="text"
                      value={formData.name}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                      メールアドレス <span className="text-red-500">*</span>
                    </label>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>
                  <div>
                    <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                      電話番号 <span className="text-red-500">*</span>
                    </label>
                    <input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>
                  <div>
                    <label htmlFor="company_name" className="block text-sm font-medium text-gray-700 mb-1">
                      会社名 <span className="text-red-500">*</span>
                    </label>
                    <input
                      id="company_name"
                      name="company_name"
                      type="text"
                      value={formData.company_name}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>

                  <div>
                    <label htmlFor="position" className="block text-sm font-medium text-gray-700 mb-1">
                      役職
                    </label>
                    <input
                      id="position"
                      name="position"
                      type="text"
                      value={formData.position}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label htmlFor="form-message" className="block text-sm font-medium text-gray-700 mb-1">
                      関心がある点を教えて下さい。
                    </label>
                    <textarea
                      id="form-message"
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      rows="3"
                      className="w-full px-2 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    ></textarea>
                  </div>
                </div>

                <div className="mt-6 flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={handleCloseModal}
                    className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500"
                  >
                    キャンセル
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    OK
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* 通知メッセージ */}
      {showNotification && (
        <div className="fixed bottom-4 right-4 bg-green-500 text-white px-6 py-3 rounded-md shadow-lg z-50 animate-fade-in-out">
          資料請求を受け付けました。担当者より折り返しご連絡いたします。
        </div>
      )}

      {/* CSSアニメーション用のスタイル */}
      <style jsx>{`
        @keyframes fadeInOut {
          0% { opacity: 0; transform: translateY(10px); }
          10% { opacity: 1; transform: translateY(0); }
          90% { opacity: 1; transform: translateY(0); }
          100% { opacity: 0; transform: translateY(-10px); }
        }
        .animate-fade-in-out {
          animation: fadeInOut 3s ease-in-out;
        }
      `}</style>
    </div>
  )
}
